import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";

/**
 * PageVisibilityContext — global map of {page_key: is_visible} loaded once from
 * `/api/pages/visibility`. Admins bypass all restrictions; non-admins get hidden
 * pages filtered out of the nav and redirected away from hidden routes.
 */
const PageVisibilityContext = createContext({
  visibility: {},
  loading: true,
  isVisible: () => true,
  reload: () => {},
});

export const PageVisibilityProvider = ({ children }) => {
  const [visibility, setVisibility] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pages/visibility`);
      setVisibility(data || {});
    } catch (e) {
      // Fail-open: if the API is unreachable, treat every page as visible so
      // we don't block navigation. Errors are logged for observability.
      console.error("Page visibility load failed", e);
      setVisibility({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isVisible = useCallback(
    (page_key) => {
      if (!page_key) return true;
      const v = visibility[page_key];
      // Default to visible when unknown so brand-new pages aren't hidden until
      // an admin explicitly toggles them.
      return v === undefined ? true : v;
    },
    [visibility],
  );

  return (
    <PageVisibilityContext.Provider value={{ visibility, loading, isVisible, reload: load }}>
      {children}
    </PageVisibilityContext.Provider>
  );
};

export const usePageVisibility = () => useContext(PageVisibilityContext);

export default PageVisibilityContext;
